import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { FaEye, FaEyeSlash } from "react-icons/fa";

import { getAdminDetails, updateAdminProfile } from "../services/profileService";
import { changePassword } from "../services/changePasswordService";
import "./ViewProfileLayer.css";

/* ================= IMAGE CONFIG ================= */
const IMAGE_BASE_URL = "https://acc.ayvin.co.in/api/uploads/admin/";
const DEFAULT_IMG = "assets/images/user-grid/user-grid-img13.png";

const makeImageUrl = (photo) => {
  if (!photo) return DEFAULT_IMG;
  return IMAGE_BASE_URL + photo;
};

const ViewProfileLayer = () => {
  const [profile, setProfile] = useState(null);
  const [imagePreview, setImagePreview] = useState(DEFAULT_IMG);
  const [selectedImage, setSelectedImage] = useState(null);
  const [errors, setErrors] = useState({});
  const [activeTab, setActiveTab] = useState("profile");

  const [showOld, setShowOld] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  useEffect(() => {
    fetchProfile();
  }, []);

  /* ================= FETCH PROFILE ================= */
  const fetchProfile = async () => {
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      const res = await getAdminDetails(user.id);

      if (res?.status === "success") {
        const d = res.data;

        const normalized = {
          id: d.id,
          name: d.name || "",
          email: d.email || "",
          phone: d.phone_number || "",
          photo: makeImageUrl(d.photo),
        };

        setProfile(normalized);
        setImagePreview(normalized.photo);
      }
    } catch {
      toast.error("Failed to load profile");
    }
  };

  /* ================= IMAGE PREVIEW ================= */
  const readURL = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedImage(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  /* ================= PROFILE UPDATE ================= */
  const handleProfileUpdate = async () => {
    let e = {};

    if (!profile.name.trim()) e.name = "Name is required";
    if (!profile.email.trim()) e.email = "Email is required";
    else if (!/\S+@\S+\.\S+/.test(profile.email))
      e.email = "Invalid email format";
    if (!profile.phone.trim()) e.phone = "Phone is required";
    else if (!/^[0-9]{10}$/.test(profile.phone))
      e.phone = "Enter valid 10-digit phone";

    setErrors(e);
    if (Object.keys(e).length) return;

    try {
      const fd = new FormData();
      fd.append("id", profile.id);
      fd.append("name", profile.name);
      fd.append("email", profile.email);
      fd.append("phone", profile.phone);
      if (selectedImage) fd.append("photo", selectedImage);

      const res = await updateAdminProfile(fd);
      if (res?.status === "success") {
        toast.success("Profile updated");
        fetchProfile();
      } else {
        toast.error(res.message || "Update failed");
      }
    } catch {
      toast.error("Update error");
    }
  };

  /* ================= CHANGE PASSWORD ================= */
  const handlePasswordChange = async () => {
    const oldPass = document.getElementById("old-pass").value;
    const newPass = document.getElementById("new-pass").value;
    const confirmPass = document.getElementById("confirm-pass").value;

    if (!oldPass || !newPass || !confirmPass) {
      toast.error("All fields required");
      return;
    }
    if (newPass.length < 6) {
      toast.error("Minimum 6 characters");
      return;
    }
    if (newPass !== confirmPass) {
      toast.error("Passwords do not match");
      return;
    }

    const res = await changePassword({
      old_password: oldPass,
      new_password: newPass,
      confirm_password: confirmPass,
    });

    if (res.status === "success") {
      toast.success("Password updated");
      document.getElementById("old-pass").value = "";
      document.getElementById("new-pass").value = "";
      document.getElementById("confirm-pass").value = "";
    } else {
      toast.error(res.message);
    }
  };

  if (!profile) return <p>Loading...</p>;

  return (
    <div className="profile-wrapper">
      {/* HEADER */}
      <div className="profile-glass-header">
        <div className="header-content">
          <img
            src={imagePreview}
            alt="avatar"
            className="header-avatar"
            onError={(e) => (e.target.src = DEFAULT_IMG)}
          />
          <div>
            <h3>Hello, {profile.name}</h3>
            <p>Manage your account</p>
          </div>
        </div>
      </div>

      <div className="row mt-4 g-4">
        {/* LEFT */}
        <div className="col-lg-4">
          <div className="profile-card">
            <img
              src={imagePreview}
              alt="profile"
              className="profile-avatar"
              onError={(e) => (e.target.src = DEFAULT_IMG)}
            />
            <h4>{profile.name}</h4>
            <p>{profile.email}</p>
            <p>{profile.phone}</p>

            <button
              className="premium-btn w-100 mt-3"
              onClick={() => setActiveTab("security")}
            >
              Change Password
            </button>
          </div>
        </div>

        {/* RIGHT */}
        <div className="col-lg-8">
          <div className="profile-right-card">
            {/* TABS */}
            <div className="tab-buttons">
              <button
                className={activeTab === "profile" ? "active" : ""}
                onClick={() => setActiveTab("profile")}
              >
                Edit Profile
              </button>
              <button
                className={activeTab === "security" ? "active" : ""}
                onClick={() => setActiveTab("security")}
              >
                Security
              </button>
            </div>

            {/* PROFILE TAB */}
            {activeTab === "profile" && (
              <>
                <div className="upload-container">
                  <div
                    className="upload-preview"
                    style={{ backgroundImage: `url(${imagePreview})` }}
                  />
                  <label className="upload-btn">
                    <input type="file" hidden onChange={readURL} />
                    Change Photo
                  </label>
                </div>

                <div className="row mt-4">
                  <div className="col-md-6 mb-3">
                    <label>Name</label>
                    <input
                      className="premium-input"
                      value={profile.name}
                      onChange={(e) =>
                        setProfile({ ...profile, name: e.target.value })
                      }
                    />
                    {errors.name && <small className="text-danger">{errors.name}</small>}
                  </div>

                  <div className="col-md-6 mb-3">
                    <label>Email</label>
                    <input
                      className="premium-input"
                      value={profile.email}
                      onChange={(e) =>
                        setProfile({ ...profile, email: e.target.value })
                      }
                    />
                    {errors.email && (
                      <small className="text-danger">{errors.email}</small>
                    )}
                  </div>

                  <div className="col-md-6 mb-3">
                    <label>Phone</label>
                    <input
                      className="premium-input"
                      value={profile.phone}
                      onChange={(e) =>
                        setProfile({ ...profile, phone: e.target.value })
                      }
                    />
                    {errors.phone && (
                      <small className="text-danger">{errors.phone}</small>
                    )}
                  </div>
                </div>

                <button className="premium-btn" onClick={handleProfileUpdate}>
                  Save Changes
                </button>
              </>
            )}

            {/* SECURITY TAB */}
            {activeTab === "security" && (
              <>
                <h5 className="mb-3">Change Password</h5>

                <div className="password-field">
                  <input
                    id="old-pass"
                    type={showOld ? "text" : "password"}
                    className="premium-input"
                    placeholder="Current Password"
                  />
                  <span onClick={() => setShowOld(!showOld)}>
                    {showOld ? <FaEyeSlash /> : <FaEye />}
                  </span>
                </div>

                <div className="password-field">
                  <input
                    id="new-pass"
                    type={showNew ? "text" : "password"}
                    className="premium-input"
                    placeholder="New Password"
                  />
                  <span onClick={() => setShowNew(!showNew)}>
                    {showNew ? <FaEyeSlash /> : <FaEye />}
                  </span>
                </div>

                <div className="password-field">
                  <input
                    id="confirm-pass"
                    type={showConfirm ? "text" : "password"}
                    className="premium-input"
                    placeholder="Confirm Password"
                  />
                  <span onClick={() => setShowConfirm(!showConfirm)}>
                    {showConfirm ? <FaEyeSlash /> : <FaEye />}
                  </span>
                </div>

                <button className="premium-btn" onClick={handlePasswordChange}>
                  Update Password
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewProfileLayer;
